import React, { useState, useEffect } from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import * as FiIcons from 'react-icons/fi';

const {FiMenu, FiBell, FiUser, FiSearch, FiActivity, FiCommand, FiSun, FiMoon} = FiIcons;

const Header = ({onMenuClick}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { theme, toggleTheme, isDark } = useTheme();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <header className="bg-[#0a0f0d]/80 backdrop-blur-md border-b border-[#243129] px-4 py-3 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg text-[#6b8772] hover:text-[#84cc16] hover:bg-[#1a2420] transition-colors"
          >
            <SafeIcon icon={FiMenu} className="w-5 h-5" />
          </button>

          {/* Search */}
          <div className="hidden md:flex items-center">
            <div className="relative group">
              <SafeIcon icon={FiSearch} className="w-4 h-4 text-[#6b8772] absolute left-3 top-1/2 transform -translate-y-1/2 group-focus-within:text-[#84cc16] transition-colors" />
              <input
                type="text"
                placeholder="Search products, orders, customers..."
                className="pl-10 pr-4 py-2 bg-[#111916] border border-[#243129] rounded-lg text-[#e8f5e9] placeholder-[#4a5f50] font-mono text-sm w-80 focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[rgba(132,204,22,0.2)] transition-all"
              />
              {/* Keyboard shortcut hint */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 text-[#4a5f50]">
                <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-[#1a2420] border border-[#243129] rounded">
                  <SafeIcon icon={FiCommand} className="w-2.5 h-2.5 inline" />
                </kbd>
                <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-[#1a2420] border border-[#243129] rounded">K</kbd>
              </div>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Live sync indicator */}
          <div className="hidden sm:flex items-center gap-2 bg-[#111916] border border-[#243129] px-3 py-1.5 rounded-lg">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <SafeIcon icon={FiActivity} className="w-4 h-4 text-[#84cc16]" />
              <div className="absolute inset-0 w-4 h-4 text-[#84cc16] animate-ping opacity-30">
                <SafeIcon icon={FiActivity} className="w-4 h-4" />
              </div>
            </motion.div>
            <span className="text-xs font-mono text-[#84cc16] font-medium tracking-wide">LIVE</span>
          </div>

          {/* Time display - terminal style */}
          <div className="hidden sm:flex flex-col items-end bg-[#111916] border border-[#243129] px-3 py-1 rounded-lg">
            <span className="text-sm font-mono text-[#84cc16] font-medium tabular-nums tracking-wider">
              {formatTime(currentTime)}
            </span>
            <span className="text-[10px] font-mono text-[#6b8772] uppercase tracking-wider">
              {formatDate(currentTime)}
            </span>
          </div>

          {/* Theme Toggle */}
          <motion.button
            onClick={toggleTheme}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-[#6b8772] hover:text-[#84cc16] hover:bg-[#1a2420] rounded-lg transition-colors"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            <motion.div
              initial={false}
              animate={{ rotate: isDark ? 0 : 180 }}
              transition={{ duration: 0.3 }}
            >
              {isDark ? (
                <SafeIcon icon={FiSun} className="w-5 h-5" />
              ) : (
                <SafeIcon icon={FiMoon} className="w-5 h-5" />
              )}
            </motion.div>
          </motion.button>

          {/* Notifications */}
          <button className="relative p-2 text-[#6b8772] hover:text-[#84cc16] hover:bg-[#1a2420] rounded-lg transition-colors">
            <SafeIcon icon={FiBell} className="w-5 h-5" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-[#0a0f0d]"></span>
          </button>

          {/* Divider */}
          <div className="hidden sm:block h-8 w-px bg-[#243129]" />

          {/* User profile */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <p className="text-sm font-mono font-medium text-[#e8f5e9]">Ops Manager</p>
              <p className="text-[10px] font-mono text-[#6b8772] uppercase tracking-wider">Fresh Produce Co.</p>
            </div>
            <div className="w-9 h-9 bg-gradient-to-br from-lime-400 to-lime-600 rounded-lg flex items-center justify-center shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow cursor-pointer">
              <SafeIcon icon={FiUser} className="w-4 h-4 text-[#050807]" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
